import {
  MailSequenceEnrollmentActivityDataFragment,
  MailSequenceEnrollmentDataFragment,
  MailSequenceTemplateDataFragment,
  MailSequenceScheduledSendDataFragment,
  MailSequenceActivityInput,
  MailSequenceEnrollmentStatus,
  MailSequenceReferenceDateType,
  MailSequenceScheduledSendStatus,
  MailSequenceSkipReason,
} from '../../generated/graphql';
import {
  MailAlertItem,
  mailSegment,
  textSegment,
} from '../components/MailAlertMessage';
import { SavedActivityInputPreview } from './followUp';
import { getShortDate } from './time';

export const REFERENCE_DATE_OPTIONS: Array<{
  value: MailSequenceReferenceDateType;
  label: string;
  helper: string;
}> = [
  {
    value: MailSequenceReferenceDateType.ProgramStartDate,
    label: 'Program start date',
    helper: "Uses each client's program start date.",
  },
  {
    value: MailSequenceReferenceDateType.ProgramEndDate,
    label: 'Program end date',
    helper: "Uses each client's program end date (e.g. retreat end).",
  },
  {
    value: MailSequenceReferenceDateType.Custom,
    label: 'Specific date',
    helper: '',
  },
];

export const referenceDateLabel = (
  type: MailSequenceReferenceDateType,
): string =>
  REFERENCE_DATE_OPTIONS.find((opt) => opt.value === type)?.label ?? type;

/** Calendar-day offset from reference date to scheduled send date. */
export const computeOffsetDaysFromDates = (
  referenceDateIso: string,
  scheduledDateIso: string,
): number => {
  const refDay = referenceDateIso.slice(0, 10);
  const schedDay = scheduledDateIso.slice(0, 10);
  const refMs = Date.parse(`${refDay}T00:00:00Z`);
  const schedMs = Date.parse(`${schedDay}T00:00:00Z`);
  if (Number.isNaN(refMs) || Number.isNaN(schedMs)) return 0;
  return Math.round((schedMs - refMs) / (24 * 60 * 60 * 1000));
};

export const DEFAULT_TEMPLATE_REFERENCE_DATE: MailSequenceReferenceDateType =
  MailSequenceReferenceDateType.ProgramStartDate;

export const DEFAULT_SEND_TIME = '09:00';

/** Matches TIME_TRIGGER_OFFSET_MAX, the clamp used by the offset UI control. */
export const MAX_OFFSET_DAYS = 365;

export type TemplateStepDraft = {
  id?: string;
  sortOrder: number;
  offsetDays: number;
  subject: string;
  providerNote: string;
};

/** @deprecated P5a index-based selection — migrated to {@link StepActivitiesState}. */
export type StepActivitySelection = Record<number, number[] | null>;

/** Full activity list per customized step, or Mail #1 extras when inheriting. */
export type StepActivitiesState = Record<number, SavedActivityInputPreview[]>;

export type StepResourceMode = 'inherit_mail_one' | 'custom';

export type StepResourceModeState = Record<number, StepResourceMode>;

export const getStepResourceMode = (
  modes: StepResourceModeState,
  stepIndex: number,
): StepResourceMode => modes[stepIndex] ?? 'inherit_mail_one';

export const activityFingerprint = (
  activity: SavedActivityInputPreview,
): string => {
  const raw = activity.sendFollowUpActivityInputRaw;
  return [
    raw.activityType,
    raw.assessmentId,
    raw.activityPDFId,
    raw.activityLinkId,
    raw.activityAudioId,
    raw.activityImageId,
    raw.activityTextId,
    raw.dateCreated,
  ]
    .filter((v) => v != null && v !== '')
    .join('|');
};

export const activitiesFingerprint = (
  activities: SavedActivityInputPreview[],
): string => activities.map(activityFingerprint).join('|');

/**
 * Like {@link activityFingerprint} but also covers editable content
 * (provider note, completability, signer assignments), so edits that keep
 * the same underlying resource still register as changes.
 */
export const activityContentFingerprint = (
  activity: SavedActivityInputPreview,
): string => {
  const raw = activity.sendFollowUpActivityInputRaw;
  return [
    activityFingerprint(activity),
    raw.providerNote ?? '',
    String(raw.isCompletable ?? true),
    JSON.stringify(raw.programAssessmentSignatureUsers ?? null),
  ].join('~');
};

export const activitiesContentFingerprint = (
  activities: SavedActivityInputPreview[],
): string => activities.map(activityContentFingerprint).join('|');

export const activityListsMatchDefault = (
  list: SavedActivityInputPreview[],
  defaultActivities: SavedActivityInputPreview[],
): boolean => {
  if (list.length !== defaultActivities.length) return false;
  const fps = list.map(activityFingerprint);
  const defaultFps = defaultActivities.map(activityFingerprint);
  return fps.every((fp, i) => fp === defaultFps[i]);
};

export const stepHasCustomActivities = (
  state: StepActivitiesState,
  stepIndex: number,
): boolean => stepIndex in state;

export const getStepEditorActivities = (
  state: StepActivitiesState,
  stepIndex: number,
): SavedActivityInputPreview[] => state[stepIndex] ?? [];

export const buildEnrollStepActivitiesFromState = (
  state: StepActivitiesState,
  modes: StepResourceModeState,
  stepIndex: number,
  defaultActivities: SavedActivityInputPreview[],
  toInputs: (
    activities: SavedActivityInputPreview[],
  ) => MailSequenceActivityInput[],
): MailSequenceActivityInput[] | undefined => {
  const mode = getStepResourceMode(modes, stepIndex);

  if (mode === 'custom') {
    if (!stepHasCustomActivities(state, stepIndex)) {
      return undefined;
    }
    return toInputs(state[stepIndex] ?? []);
  }

  const extras = state[stepIndex] ?? [];
  if (extras.length === 0) {
    return undefined;
  }

  return toInputs(
    mergeStepActivitiesWithComposerOrder(
      [...defaultActivities, ...extras],
      defaultActivities,
    ),
  );
};

export const mergeStepActivitiesWithComposerOrder = (
  selected: SavedActivityInputPreview[],
  composerActivities: SavedActivityInputPreview[],
): SavedActivityInputPreview[] => {
  const selectedFps = new Set(selected.map(activityFingerprint));
  const fromComposer = composerActivities.filter((a) =>
    selectedFps.has(activityFingerprint(a)),
  );
  const stepOnly = selected.filter(
    (a) =>
      !composerActivities.some(
        (c) => activityFingerprint(c) === activityFingerprint(a),
      ),
  );
  return [...fromComposer, ...stepOnly];
};

export const migrateStepActivitySelection = (
  legacy: StepActivitySelection | undefined,
  defaultActivities: SavedActivityInputPreview[],
): StepActivitiesState => {
  if (!legacy || !Object.keys(legacy).length) return {};
  const state: StepActivitiesState = {};
  Object.entries(legacy).forEach(([key, indices]) => {
    const stepIndex = Number(key);
    if (indices === null || indices === undefined) return;
    const list = indices
      .map((i) => defaultActivities[i])
      .filter((a): a is SavedActivityInputPreview => Boolean(a));
    if (!activityListsMatchDefault(list, defaultActivities)) {
      state[stepIndex] = list;
    }
  });
  return state;
};

export type TemplateDraft = {
  id?: string;
  name: string;
  steps: TemplateStepDraft[];
};

export const blankStep = (
  sortOrder = 0,
  offsetDays = 30,
): TemplateStepDraft => ({
  sortOrder,
  offsetDays,
  subject: '',
  providerNote: '',
});

/** Default offset for a newly added step: 30 days after the last one, capped. */
export const nextStepOffset = (steps: TemplateStepDraft[]): number => {
  const lastOffset = steps[steps.length - 1]?.offsetDays ?? 0;
  return lastOffset >= 0
    ? Math.min(lastOffset + 30, MAX_OFFSET_DAYS)
    : Math.min(lastOffset + 30, 0);
};

export const isValidSequenceStepOffset = (
  offsetDays: number,
  maxOffsetDays: number,
): boolean =>
  Number.isFinite(offsetDays) && Math.abs(offsetDays) <= maxOffsetDays;

/**
 * Offset/subject/message checks shared by the enroll page and the sequence
 * template editor. Callers layer their context-specific checks (recipients,
 * template name, ...) around these.
 */
export const buildStepValidationErrors = (
  steps: TemplateStepDraft[],
  anchorLabel: string,
): MailAlertItem[] => {
  const errs: MailAlertItem[] = [];
  if (!steps.length) {
    errs.push({
      key: 'no-mail',
      segments: [textSegment('Add at least one Mail to this sequence.')],
    });
  }
  steps.forEach((s, i) => {
    if (!isValidSequenceStepOffset(s.offsetDays, MAX_OFFSET_DAYS)) {
      errs.push({
        key: `offset-${i}`,
        segments: [
          mailSegment(i),
          textSegment(
            ` timing must be within ${MAX_OFFSET_DAYS} days of the ${anchorLabel}.`,
          ),
        ],
      });
    }
    if (!s.subject.trim()) {
      errs.push({
        key: `subject-${i}`,
        segments: [mailSegment(i), textSegment(' needs a subject.')],
      });
    }
    if (!s.providerNote.trim()) {
      errs.push({
        key: `message-${i}`,
        segments: [mailSegment(i), textSegment(' needs a message.')],
      });
    }
  });
  return errs;
};

export type MailSequenceTemplate = MailSequenceTemplateDataFragment;
export type SequenceEnrollment = MailSequenceEnrollmentDataFragment;
export type SequenceScheduledSend = MailSequenceScheduledSendDataFragment;
export type SequenceEnrollmentActivity =
  MailSequenceEnrollmentActivityDataFragment;

export const templateToDraft = (
  preset: MailSequenceTemplateDataFragment,
): TemplateDraft => ({
  id: preset.id,
  name: preset.name,
  steps: [...preset.steps]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((step) => ({
      id: step.id,
      sortOrder: step.sortOrder,
      offsetDays: step.offsetDays,
      subject: step.subject,
      providerNote: step.providerNote,
    })),
});

export const normalizeTimeString = (time: string): string => {
  if (!time) return DEFAULT_SEND_TIME;
  const [hh, mm] = time.split(':');
  const h = (hh ?? '09').padStart(2, '0').slice(0, 2);
  const m = (mm ?? '00').padStart(2, '0').slice(0, 2);
  return `${h}:${m}`;
};

export const sequenceDisplayTitle = (
  enrollment: Pick<MailSequenceEnrollmentDataFragment, 'name' | 'createdAt'>,
): string => {
  const named = enrollment.name?.trim();
  if (named) return named;
  return `Mail sequence (Enrolled on ${getShortDate(enrollment.createdAt)})`;
};

/**
 * Timing word for sequence list captions based on where scheduled dates fall
 * relative to the enrollment reference date.
 */
export const sequenceScheduleTiming = (
  referenceDate: string,
  scheduledDates: string[],
): 'before' | 'after' | 'around' => {
  const ref = referenceDate.slice(0, 10);
  let hasBefore = false;
  let hasAfter = false;
  let hasOnOrEmpty = scheduledDates.length === 0;

  for (const date of scheduledDates) {
    const day = date.slice(0, 10);
    if (day < ref) hasBefore = true;
    else if (day > ref) hasAfter = true;
    else hasOnOrEmpty = true;
  }

  if (hasBefore && !hasAfter && !hasOnOrEmpty) return 'before';
  if (hasAfter && !hasBefore && !hasOnOrEmpty) return 'after';
  return 'around';
};

export const ENROLLMENT_STATUS_LABEL: Record<
  MailSequenceEnrollmentStatus,
  string
> = {
  [MailSequenceEnrollmentStatus.Active]: 'Scheduled',
  [MailSequenceEnrollmentStatus.Completed]: 'Completed',
  [MailSequenceEnrollmentStatus.Cancelled]: 'Cancelled',
};

export const SCHEDULED_SEND_STATUS_LABEL: Record<
  MailSequenceScheduledSendStatus,
  string
> = {
  [MailSequenceScheduledSendStatus.Pending]: 'Scheduled',
  [MailSequenceScheduledSendStatus.Sending]: 'Sending',
  [MailSequenceScheduledSendStatus.Sent]: 'Sent',
  [MailSequenceScheduledSendStatus.Failed]: 'Failed',
  [MailSequenceScheduledSendStatus.Skipped]: 'Skipped',
  [MailSequenceScheduledSendStatus.Cancelled]: 'Cancelled',
};

export const SKIP_REASON_LABEL: Record<MailSequenceSkipReason, string> = {
  [MailSequenceSkipReason.PastDueAtEnroll]: 'Step date already passed',
  [MailSequenceSkipReason.PastDueBeyondLookback]:
    'Step missed beyond retry window',
  [MailSequenceSkipReason.ProgramDeactivated]: 'Program is no longer active',
  [MailSequenceSkipReason.PatientRemoved]: 'Patient was removed',
  [MailSequenceSkipReason.ProviderDeleted]: 'Provider account deleted',
  [MailSequenceSkipReason.AssessmentArchived]: 'Assessment archived',
  [MailSequenceSkipReason.ResourceArchived]: 'Resource archived',
  [MailSequenceSkipReason.ResourceUnavailable]: 'Resource unavailable',
  [MailSequenceSkipReason.ProviderOnlyAfterEnroll]:
    'Assessment became provider-only',
  [MailSequenceSkipReason.SignerUnavailable]:
    'Required team member is no longer assigned to this client',
  [MailSequenceSkipReason.EnrollerDisabled]:
    'Enrolling provider is no longer active',
  [MailSequenceSkipReason.SendFailed]: 'Send failed after retries',
};
